import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Edit3,
  ImageOff,
  Package,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { apiDelete, apiGet } from "@/services/api";
import { formatPrice } from "@/lib/utils";

type Product = {
  id: string;
  title: string;
  price: number;
  stock: number;
  status?: string;
  category: string;
  images?: string[] | string;
  rejectionReason?: string | null;
  createdAt?: string;
};

export function VendorProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const data = await apiGet<{ products: Product[] }>("/api/vendors/me/products");
      setProducts(data?.products ?? []);
    } catch (err) {
      console.error("Failed to load products:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function remove(id: string) {
    if (!confirm("Are you sure you want to remove this product?")) return;
    setDeletingId(id);
    try {
      await apiDelete(`/api/vendors/me/products/${id}`);
      toast.success("Product removed");
      void load();
    } catch {
      toast.error("Could not delete product");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white">
            My Product Catalog
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Manage your submitted products, view live approval status, and update stock or pricing.
          </p>
        </div>
        <Link to="/vendor/products/new" className="w-full sm:w-auto">
          <Button className="w-full sm:w-auto h-10 gap-2 bg-[#087DB5] hover:bg-[#073B70] text-white font-bold text-xs sm:text-sm shadow-xs active:scale-95 transition">
            <Plus className="h-4 w-4" />
            Add New Product
          </Button>
        </Link>
      </div>

      {/* Product List */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-xs dark:border-white/10 dark:bg-slate-900">
        {loading ? (
          <div className="p-10 sm:p-12 text-center text-sm text-slate-500 dark:text-slate-400">
            Loading your products…
          </div>
        ) : products.length === 0 ? (
          <div className="p-8 sm:p-12 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500">
              <Package className="h-7 w-7" />
            </div>
            <h3 className="mt-3 text-base font-bold text-slate-900 dark:text-white">
              No products yet
            </h3>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
              Submit your first product for review. Once approved by the MOVA admin team, it will appear live in the customer marketplace.
            </p>
            <Link to="/vendor/products/new" className="inline-block mt-4 w-full sm:w-auto">
              <Button size="sm" className="w-full sm:w-auto gap-1.5 bg-[#087DB5] hover:bg-[#073B70] text-xs font-bold text-white shadow-xs">
                <Plus className="h-3.5 w-3.5" />
                Submit Your First Product
              </Button>
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-white/5">
            {products.map((p) => {
              const coverImage = Array.isArray(p.images)
                ? p.images[0]
                : typeof p.images === "string"
                ? p.images.split("\n")[0]
                : null;

              const isPending = p.status === "PENDING" || p.status === "PENDING_REVIEW";
              const isRejected = p.status === "REJECTED";

              return (
                <div
                  key={p.id}
                  className="flex flex-col gap-3 p-3.5 sm:p-5 transition hover:bg-slate-50/60 dark:hover:bg-slate-800/40"
                >
                  <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                    {/* Thumbnail */}
                    <div className="relative h-14 w-14 sm:h-16 sm:w-16 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-800 flex items-center justify-center">
                      {coverImage ? (
                        <img
                          src={coverImage}
                          alt={p.title}
                          className="h-full w-full object-contain p-1"
                        />
                      ) : (
                        <ImageOff className="h-6 w-6 text-slate-400" />
                      )}
                    </div>

                    {/* Details */}
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-slate-900 dark:text-white line-clamp-2 text-xs sm:text-base leading-snug">
                        {p.title}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5 sm:gap-2 text-xs text-slate-500 dark:text-slate-400">
                        <span className="font-semibold text-slate-700 dark:text-slate-300 rounded bg-slate-100 dark:bg-white/10 px-1.5 py-0.5 text-[10px] sm:text-xs">
                          {p.category}
                        </span>
                        <span>·</span>
                        <span>Stock: <strong className="text-slate-700 dark:text-slate-300">{p.stock}</strong></span>
                        <span>·</span>
                        <span className="font-black text-slate-900 dark:text-white text-xs sm:text-sm">
                          {formatPrice(p.price)}
                        </span>
                      </div>

                      {/* Rejection notice if any */}
                      {isRejected && p.rejectionReason && (
                        <div className="mt-2 rounded-lg border border-rose-200 bg-rose-50/80 p-2 text-xs text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/40 dark:text-rose-300">
                          <p className="font-bold">Admin review feedback:</p>
                          <p className="mt-0.5">{p.rejectionReason}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Status & Action Buttons Bar */}
                  <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-100 dark:border-white/5 sm:border-t-0 sm:pt-0">
                    <div>
                      {isPending ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2 sm:px-2.5 py-1 text-[11px] sm:text-xs font-bold text-amber-800 dark:border-amber-700/50 dark:bg-amber-950/60 dark:text-amber-300">
                          <Clock className="h-3.5 w-3.5 animate-pulse" />
                          <span>Under Review</span>
                        </span>
                      ) : isRejected ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-300 bg-rose-50 px-2 sm:px-2.5 py-1 text-[11px] sm:text-xs font-bold text-rose-800 dark:border-rose-700/50 dark:bg-rose-950/60 dark:text-rose-300">
                          <AlertCircle className="h-3.5 w-3.5" />
                          <span>Needs Revision</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-2 sm:px-2.5 py-1 text-[11px] sm:text-xs font-bold text-emerald-800 dark:border-emerald-700/50 dark:bg-emerald-950/60 dark:text-emerald-300">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          <span>Approved & Live</span>
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Link
                        to={`/vendor/products/${p.id}`}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:bg-slate-800 dark:text-slate-300 shadow-2xs active:scale-95 transition"
                      >
                        <Edit3 className="h-3.5 w-3.5" />
                        <span>Edit</span>
                      </Link>

                      <button
                        type="button"
                        onClick={() => remove(p.id)}
                        disabled={deletingId === p.id}
                        className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-bold text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/50 disabled:opacity-50 active:scale-95 transition cursor-pointer"
                        title="Remove Product"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
